import { ChangeDetectorRef, Component, Input, OnInit, ViewChild } from "@angular/core";
import { VTableComponent } from "@app/directives/_layout/v-table/_components/v-table.component";
import { IVTableColumn } from "@app/directives/_layout/v-table/_models/IVTableColumn";
import { IVTableRow } from "@app/directives/_layout/v-table/_models/IVTableRow";
import { VTableService } from "@app/directives/_layout/v-table/_services/v-table.service";
import { IAggregateFilterEvent } from "@app/directives/_resourceFilters/filter-bar/filter-bar.models";
import { IPill, VPillsComponent } from "@app/directives/_veri/v-pills/v-pills.component";
import { SortDirection } from "@app/enum/SortDirection";
import { IGoogleMapsMarker } from "@app/interfaces/IGoogleMapsMarkerOptions";
import { IPlot } from "@app/interfaces/IPlot";
import { ICameraConfiguration } from "@app/main/modules/foundation/_routes/rv-lite/config/_models/ICameraConfiguration";
import { ConfigManagerService } from "@app/main/modules/foundation/_routes/rv-lite/config/_services/config.service";
import { DateService, DateUnit } from "@app/services/date.service";
import { MapService } from "@app/services/map.service";
import { TranslationService } from "@app/services/translation.service";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { combineLatest, Observable, of, Subject } from "rxjs";
import { mergeMap, takeUntil, tap } from "rxjs/operators";
import { CameraCommandType, IPulseRecord } from "../../_models/IPulseRecord";
import { IPulseRecordPayload } from "../../_models/IPulseRecordPayload";
import './pulse-messages.component.scss';
import { PulseService } from "./pulse.service";

@UntilDestroy()
@Component({
    selector: 'pulse-messages',
    template: require('./pulse-messages.component.html')
})
export class PulseMessagesComponent implements OnInit {
    @Input() serial: string;
    @ViewChild('messagesTable') table: VTableComponent;

    messages: IPulseRecord[] = [];
    configs: ICameraConfiguration[] = [];
    tableColumns: IVTableColumn<IPulseRecord>[];
    tableRows: IVTableRow[] = [];
    exportTitle: string;

    selectedMessage: IPulseRecord;
    selectedPayload: IPulseRecordPayload;
    selectedMessagePlot: IPlot;
    selectedMessageLocation: google.maps.LatLng;
    locationMarker: IGoogleMapsMarker;

    startDate: Date;
    endDate: Date;

    loading: boolean;
    liveUpdates: boolean = true;
    cancelLiveUpdates$ = new Subject<void>();

    CameraCommandType = CameraCommandType;

    constructor(
        private readonly cd: ChangeDetectorRef,
        private readonly dateService: DateService,
        private readonly mapService: MapService,
        private readonly pulseService: PulseService,
        private readonly configManagerService: ConfigManagerService,
        private readonly translationService: TranslationService,
        private readonly vTableService: VTableService) { }

    ngOnInit(): void {
        this.initDates();
        this.createTable();
        this.getHistoricalMessagesAndSubscribeToNew();
    }

    showMessageDetail(row: IVTableRow): void {
        this.selectedMessage = row.data;
        this.selectedMessagePlot = this.createPlot(this.selectedMessage);
        this.selectedMessageLocation = this.createLocation(this.selectedMessage);
        this.selectedPayload = this.selectedMessage.parsedPayload || this.getParsedPayload(this.selectedMessage);

        const label = (this.selectedPayload.GPS?.speed || 0).toString();
        this.locationMarker = this.mapService.createMapMarker(this.selectedMessagePlot, { label: label, showLabel: true });
    }

    clearSelectedMessage(): void {
        this.selectedMessage = null;
        this.selectedMessagePlot = null;
        this.locationMarker = null;
    }

    onFilterChanged(filterEvent: IAggregateFilterEvent<IPulseRecord>): void {
        if (filterEvent.trigger == "date") {
            this.cancelLiveUpdates$.next();
            this.startDate = filterEvent.date[0];
            this.endDate = filterEvent.date[1];
            this.setLiveUpdates();
            this.getHistoricalMessagesAndSubscribeToNew();
            return;
        }
    }


    //--

    private getHistoricalMessagesAndSubscribeToNew(): void {
        this.loading = true;

        combineLatest([
            this.pulseService.getHistoricalPulseMessages({ Serial: this.serial, Start: this.startDate, End: this.endDate }),
            this.configManagerService.getConfigs()
        ]).pipe(
            untilDestroyed(this),
            tap(([historicalMessages, configs]) => {
                this.messages = historicalMessages;
                this.configs = configs;

                this.tableRows = this.vTableService.createTableRows(this.messages, this.tableColumns);
                this.createExportTitle();

                this.loading = false;

                return;
            })).subscribe();
    }

    private createTable(): void {
        this.tableColumns = [
            {
                header: this.vTableService.createDateTableHeader({ sorted: true, sortDirection: SortDirection.Descending }),
                createCellFunction: message => this.vTableService.createDateCell(message.CreatedTime)
            },
            {
                header: this.vTableService.createLocationInformationHeader({ width: null }),
                createCellFunction: message => this.vTableService.createLocationInformationCell({ latLng: this.createLocation(message) })
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "FoundationPeripherals.ResponseRequests" }),
                createCellFunction: message => this.vTableService.createTableCell(this.getEnumFlagsAsString(message.ResponseType), {
                    component: VPillsComponent,
                    componentInputs: {
                        pills: this.getFlagsEnumAsPills(message.ResponseType)
                    }
                })
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "FoundationPeripherals.UpdaterVersion", width: 150 }),
                createCellFunction: message => this.vTableService.createTableCell(this.getFirmwareVersion(message))
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "FoundationPeripherals.TelematicsVersion", width: 150 }),
                createCellFunction: message => this.vTableService.createTableCell(this.getTelematicsVersion(message))
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "FoundationPeripherals.ConfigVersion" }),
                createCellFunction: message => this.vTableService.createTableCell(this.getConfigVersion(message))
            }
        ]
    }

    private createPlot(message: IPulseRecord): IPlot {
        if (!message.Payload) {
            return;
        }

        message.parsedPayload = this.getParsedPayload(message);
        return {
            Latitude: +message.parsedPayload.GPS.latitude,
            Longitude: +message.parsedPayload.GPS.longitude
        }
    }

    private createLocation(message: IPulseRecord): google.maps.LatLng {
        if (!message.Payload) {
            return;
        }

        message.parsedPayload = this.getParsedPayload(message);

        return new google.maps.LatLng(+message.parsedPayload.GPS.latitude, +message.parsedPayload.GPS.longitude);

    }

    private getTelematicsVersion(message: IPulseRecord): string {
        const payload = this.getParsedPayload(message);
        const application = payload['Applications'];

        return application[0].AppVersion;
    }

    private getConfigVersion(message: IPulseRecord): string {
        const payload = this.getParsedPayload(message);

        let cameraConfigId = this.pulseService.extractGuid(payload.HardwareConfigName);

        if (!cameraConfigId) {
            var oldId = parseInt(payload.HardwareConfigName);
            if (!isNaN(oldId)) {
                return `Old Config Ref: ${oldId}`;
            }
        }

        if (!cameraConfigId) {
            return `Old Config Ref: ${payload.HardwareConfigName}`;
        }

        return this.configs.find(config => config.Id == cameraConfigId)?.Name ?? this.commServerToVerilocationConfigMappings[cameraConfigId] ?? "Unknown";
    }

    private commServerToVerilocationConfigMappings: { [key: string]: string } = {
        "C157A7C1-723D-408C-B7D9-32D27DBDE60D": "NEEDS TO BE MAPPED",
        "b2258a1a-168e-466b-ae23-6dfac432624f": "NEEDS TO BE MAPPED"
    };

    private getFirmwareVersion(message: IPulseRecord): string {
        const payload = this.getParsedPayload(message);

        return payload['FirmwareVersion'];
    }

    private getParsedPayload(message: IPulseRecord): IPulseRecordPayload {
        return JSON.parse(message.Payload);
    }

    private GetEnumFlagsAsList(flags: CameraCommandType): string[] {
        return Object.values(CameraCommandType)
            .filter((value) => typeof value === 'number' && (flags & value) !== 0)
            .map((value) => CameraCommandType[value])
            .filter((name) => name !== undefined);
    }

    private getFlagsEnumAsPills(flags: CameraCommandType): IPill<CameraCommandType>[] {
        const flagNames = this.GetEnumFlagsAsList(flags);

        let flagPills: IPill<CameraCommandType>[] = [];
        console.log(flags)
        flagNames.forEach(f => {
            flagPills.push({
                label: f.replace(/([A-Z])/g, ' $1').trim()
            });
        });

        return flagPills;
    }

    private getEnumFlagsAsString(flags: CameraCommandType): string {
        const flagNames = this.GetEnumFlagsAsList(flags);

        if (!flagNames.length) {
            return "None";
        }

        return flagNames.join(', ');
    }

    private initDates(): void {
        this.startDate = this.dateService.getStartOfDay(new Date);
        this.endDate = this.dateService.getEndOfDay(new Date)
    }

    private setLiveUpdates(): void {
        // Only subscribe to new messages if the end date is within the last 15 minutes or in the future
        this.liveUpdates = this.dateService.date1IsAfterDate2(this.endDate, this.dateService.addOrSubtractTime(new Date(), -15, DateUnit.Min));
    }

    private createExportTitle(): void {
        this.exportTitle = this.translationService.t('FoundationPeripherals.PulseMessages') + " " + this.dateService.formatDate(this.startDate);
    }
}
